import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Check, RotateCcw } from "lucide-react";
import { trackCareMatch, getCareMatchSession, resetCareMatchSession } from "../lib/careMatch";

// Quiz definition. Tone deliberately gentle, calm aesthetic.
const STEPS = [
 {
 id: "concern",
 title: "What brings you here today?",
 subtitle: "Pick what feels closest. You can change this anytime.",
 options: [
 { v: "anxiety", l: "Anxiety or panic" },
 { v: "depression", l: "Depression or low mood" },
 { v: "adhd", l: "ADHD or focus" },
 { v: "trauma", l: "Trauma / PTSD" },
 { v: "sleep", l: "Sleep concerns" },
 { v: "meds", l: "Medication management" },
 { v: "substance", l: "Substance use support" },
 { v: "unsure", l: "Not sure yet" },
 ],
 },
 {
 id: "age",
 title: "Which age range describes you?",
 options: [
 { v: "under18", l: "Under 18" },
 { v: "18_25", l: "18, 25" },
 { v: "26_40", l: "26, 40" },
 { v: "41_65", l: "41, 65" },
 { v: "65plus", l: "65+" },
 ],
 },
 {
 id: "telehealth",
 title: "Comfortable starting with secure telehealth video visits?",
 subtitle: "All our visits are virtual.",
 options: [
 { v: "yes", l: "Yes, sounds great" },
 { v: "open", l: "Open to it" },
 { v: "more_info", l: "I'd like to know more first" },
 ],
 },
 {
 id: "urgency",
 title: "How soon would you like to be seen?",
 options: [
 { v: "this_week", l: "This week" },
 { v: "two_weeks", l: "Within 2 weeks" },
 { v: "no_rush", l: "No rush" },
 ],
 },
];

const CONCERN_LABEL = {
 anxiety: "anxiety / panic",
 depression: "depression or low mood",
 adhd: "ADHD or focus",
 trauma: "trauma / PTSD",
 sleep: "sleep concerns",
 meds: "medication management",
 substance: "substance use support",
 unsure: "exploring care",
};
const AGE_LABEL = {
 under18: "under 18",
 "18_25": "18-25",
 "26_40": "26-40",
 "41_65": "41-65",
 "65plus": "65+",
};
const URGENCY_LABEL = {
 this_week: "this week",
 two_weeks: "within 2 weeks",
 no_rush: "no rush",
};

function deriveSuggestion(answers) {
 const c = answers.concern;
 let service = "Psychiatric Evaluation";
 if (c === "meds") service = "Medication Management";
 if (c === "substance") service = "Psychiatric Evaluation"; // referral path discussed in visit

 const partsForNotes = [];
 if (c) partsForNotes.push(`Care Match: looking for help with ${CONCERN_LABEL[c] || c}.`);
 if (answers.age) partsForNotes.push(`Age range: ${AGE_LABEL[answers.age] || answers.age}.`);
 if (answers.telehealth === "more_info") partsForNotes.push("Would like to learn more about telehealth before the first visit.");
 if (answers.urgency) partsForNotes.push(`Preferred timing: ${URGENCY_LABEL[answers.urgency] || answers.urgency}.`);
 return { service, notes: partsForNotes.join(" ") };
}

export default function CareMatchQuiz() {
 const [answers, setAnswers] = useState({});
 const [step, setStep] = useState(0);
 const [done, setDone] = useState(false);
 const [started, setStarted] = useState(false);
 const navigate = useNavigate();

 const total = STEPS.length;
 const progress = useMemo(() => Math.round(((done ? total : step) / total) * 100), [step, done, total]);

 const current = STEPS[step];

 function ensureStart() {
 if (!started) {
 // ensure a session is created and start event fired exactly once
 getCareMatchSession();
 trackCareMatch({ event_type: "start" });
 setStarted(true);
 }
 }

 function pick(v) {
 ensureStart();
 const next = { ...answers, [current.id]: v };
 setAnswers(next);
 trackCareMatch({ event_type: "answer", step: current.id, value: v });
 if (step + 1 < total) {
 setStep(step + 1);
 } else {
 const suggestion = deriveSuggestion(next);
 trackCareMatch({
 event_type: "complete",
 suggested_service: suggestion.service,
 answers: next,
 });
 setDone(true);
 }
 }

 function reset() {
 resetCareMatchSession();
 setAnswers({});
 setStep(0);
 setDone(false);
 setStarted(false);
 }

 function goBook() {
 const { service, notes } = deriveSuggestion(answers);
 const session_id = getCareMatchSession();
 trackCareMatch({ event_type: "book_clicked", suggested_service: service });
 navigate("/contact", { state: { prefill: { service, notes, source: "care-match", session_id } } });
 }

 const suggestion = done ? deriveSuggestion(answers) : null;

 return (
 <section data-testid="care-match" className="container-pad max-w-7xl mx-auto section">
 <div className="relative overflow-hidden card-soft border-2 border-gold-500/20 p-8 sm:p-10 lg:p-14">
 <div aria-hidden className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold-500/10 blur-3xl" />
 <div aria-hidden className="absolute -bottom-32 -left-12 w-72 h-72 rounded-full bg-sage-500/10 blur-3xl" />

 <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-14">
 <div className="lg:col-span-5">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Care Match · 60 seconds</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink text-balance">
 Not sure where to begin? <span className="italic text-sage-600">Let us help you start.</span>
 </h2>
 <p className="mt-5 text-muted_text leading-relaxed max-w-md">
 Four gentle questions. No account needed. We'll suggest the right starting point and pre-fill your booking, so you can focus on what matters.
 </p>
 <div className="mt-8 flex items-center gap-3">
 <div className="flex-1 h-1.5 rounded-full bg-cream overflow-hidden">
 <div data-testid="cm-progress" className="h-full bg-gold-500 transition-all duration-500" style={{ width: `${progress}%` }} />
 </div>
 <span className="text-xs tabular-nums text-muted_text font-medium">{progress}%</span>
 </div>
 </div>

 <div className="lg:col-span-7">
 {!done ? (
 <div data-testid={`cm-step-${current.id}`} key={current.id} className="animate-fade-up">
 <p className="text-xs uppercase tracking-[0.22em] font-semibold text-sage-700">Step {step + 1} of {total}</p>
 <h3 className="mt-3 font-heading text-2xl sm:text-3xl text-ink text-balance">{current.title}</h3>
 {current.subtitle && <p className="mt-3 text-sm text-muted_text">{current.subtitle}</p>}
 <div className="mt-7 grid sm:grid-cols-2 gap-3">
 {current.options.map((o) => (
 <button
 key={o.v}
 type="button"
 data-testid={`cm-opt-${current.id}-${o.v}`}
 onClick={() => pick(o.v)}
 className={`text-left rounded-2xl border-2 px-5 py-4 text-sm font-medium transition-all
 ${answers[current.id] === o.v
 ? "border-sage-700 bg-sage-50 text-sage-800"
 : "border-[#E8EBE4] bg-paper text-ink hover:border-sage-400 hover:bg-sage-50/40"
 }`}
 >
 <span className="inline-flex items-start gap-3">
 <span className={`mt-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0 ${answers[current.id] === o.v ? "bg-sage-700 text-white" : "border border-sage-300/70"}`}>
 {answers[current.id] === o.v && <Check className="w-3 h-3" strokeWidth={3} />}
 </span>
 {o.l}
 </span>
 </button>
 ))}
 </div>
 {step > 0 && (
 <button
 type="button"
 onClick={() => setStep(step - 1)}
 data-testid="cm-back"
 className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-muted_text hover:text-ink"
 >
 ← Go back
 </button>
 )}
 </div>
 ) : (
 <div data-testid="cm-result" className="animate-fade-up">
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage-500/15 text-sage-700 text-xs font-semibold tracking-[0.16em] uppercase">
 <Sparkles className="w-3.5 h-3.5" /> Match found
 </div>
 <h3 className="mt-5 font-heading text-2xl sm:text-3xl text-ink text-balance">
 Based on what you shared, we suggest starting with a <span className="text-sage-700 italic">{suggestion.service}</span>.
 </h3>
 <p className="mt-4 text-muted_text leading-relaxed">
 This 50-minute virtual visit gives your clinician time to listen carefully, understand what's going on, and craft a plan that fits your life.
 </p>

 <div className="mt-6 rounded-2xl border border-[#E8EBE4] bg-cream p-5 text-sm text-ink/85 leading-relaxed">
 <p data-testid="cm-summary">{suggestion.notes}</p>
 </div>

 <div className="mt-8 flex flex-wrap items-center gap-4">
 <button
 type="button"
 data-testid="cm-go-book"
 onClick={goBook}
 className="btn-gold !text-base"
 >
 Continue to booking <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
 </button>
 <button
 type="button"
 data-testid="cm-restart"
 onClick={reset}
 className="inline-flex items-center gap-2 text-sm font-medium text-muted_text hover:text-ink"
 >
 <RotateCcw className="w-4 h-4" /> Start over
 </button>
 </div>
 <p className="mt-5 text-xs text-muted_text">Your answers stay on your device unless you choose to share them on the booking page.</p>
 </div>
 )}
 </div>
 </div>
 </div>
 </section>
 );
}
